import _ from 'lodash';
import { track, identify, trackEcommerceData, trackConfiguratorQuantity, trackReducedUsersData } from './helpers';
import { convertPlanIdIntoSubscription } from '../../utils/subscription';
import { SUBSCRIPTION_PRICES, SUBSCRIPTION_PERIOD, SUBSCRIPTION_PLAN } from '../../config';

import Logger from '../../logger/logger';
const log = Logger.get('CustomTrackers');
import ERROR from 'errors/errorids';
import AppError from '../../errors/apperror';

function getCurrentTimestampInSeconds() {
	let currentMs = new Date().getTime();
	return Math.floor(currentMs / 1000);
}

const getPlanNameForTracking = (plan) => {
	return plan === SUBSCRIPTION_PLAN.TEAM_PLUS ? SUBSCRIPTION_PLAN.SCALE : plan;
};

// USERS AND PERMISSIONS
export const trackUserAdd = (users, options) => {
	track('cap-add-users', {total: users.length}, options);
};

export const trackUserDelete = (user, options) => {
	track(`cap-delete-${user.trackingName.toLowerCase()}`, user, options);
};

export const trackUserEdit = (previousUser, updatedUser, options) => {
	track('cap-change-user-role', updatedUser, options);
};

// TEAM FOLDERS
export const trackAddTeamFolder = (folder, options) => {
	track(`cap-add-${folder.type}-team-folder`, folder, options);
};

// SUBSCRIPTION
export const trackClickSubscriptionUpgrade = (plan, period, isPerpetual, options) => {
	const metaData = {
		plan: plan === SUBSCRIPTION_PLAN.TEAM_PLUS ? SUBSCRIPTION_PLAN.SCALE : plan,
		period: isPerpetual ? 'perpetual' : period
	};
	track(`cap-click-${getPlanNameForTracking(plan)}-do-checkout`, metaData, options);
};

export const trackSubscriptionSuccessful = (companyId, currentPlanId, eventName, options) => {
	track(`cap-${eventName}-successful`, null, options);

	// Ecommerce tracking
	try {
		const activeSubscription = convertPlanIdIntoSubscription(currentPlanId);
		const { plan, period, currency } = activeSubscription;
		let currentPrice = SUBSCRIPTION_PRICES[plan][period];

		if (period === SUBSCRIPTION_PERIOD.ANNUAL) {
			currentPrice *= 12;
		}

		const planName = getPlanNameForTracking(plan);

		trackEcommerceData('Order Completed', {
			// Keep this name, see https://segment.com/docs/connections/spec/ecommerce/v2/
			order_id: `${companyId}-${_.now()}`,
			currency: _.toUpper(currency),
			total: parseFloat(currentPrice),
			products: [
				{
					category: planName,
					name: `${_.upperFirst(_.toLower(planName))} ${_.upperFirst(_.toLower(period))}`,
					price: parseFloat(currentPrice),
					product_id: `${planName}-${period}`,
					quantity: 1,
					sku: `${planName}-${period}`
				}
			]
		});
	} catch (error) {
		const nError = new AppError({
			message: 'trackSubscriptionSuccessful() failed',
			errorId: ERROR.UNKNOWN,
			originalError: error,
			visualisers: { feature: 'CHARGEBEE' },
			errorDetails: {
				currentPlanId
			},				
		});
		log.send(nError);		
	}
};

export const trackVisitedReactivateRouteFromEmail = (options) => {
	track('cap-visited-reactivate-route-from-email', null, options);
};

export const trackVisitedPlusOverview = (options) => {
	track('cap-visited-plus-overview', null, options);
};

export const trackVisitedPerpetualOverview = (options) => {
	track('cap-visited-perpetual-overview', null, options);
};

export const trackVisitedStandardOverview = (options) => {
	track('cap-visited-standard-overview', null, options);
};

export const trackRouteMagixDiscount = (prodCat, options) => {
	track('cap-entered-route-magix-discount', { prodCat }, options);
};

export const trackRouteUpgradePopupDiscount = (options) => {
	track('cap-entered-route-upgrade-popup-discount', null, options);
};

export const trackRouteCouponDiscount = (cid, options) => {
	track('cap-entered-route-coupon', { cid }, null, options);
};

export const trackRouteDesktopAppUpgrade = (productName, options) => {
	track('cap-entered-route-desktop-app-upgrade', { productName }, options);
};

export const trackRouteSource = (productName, productSource, options) => {
	track(`cap-entered-route-${productSource}`, { productName }, options);
};

export const trackApplyCouponClicked = (coupon, options) => {
	track('cap-apply-coupon-clicked', { coupon }, options);
};

export const trackActiveOfferLinkClicked = (options) => {
	track('cap-active-offer-link-clicked', null, options);
};

// DESKTOP_APPS
export const trackDownloadsAsProperty = (companyName, key, options) => {
	identify({
		[`${key}-downloaded-on`]: getCurrentTimestampInSeconds(),
		[`${key}-download-company`]: companyName
	},
	options);
};

export const trackDownloadedProduct = (trackingKey, options) => {
	track('cap-downloaded-product', { product: trackingKey }, options);
};

// WRONG ACCOUNT SCREEN
export const trackWrongAccountScreen = (options) => {
	track('cap-wrong-account-screen-shown', null, options);
};

export const trackWrongAccountScreenUseExistingAccount = (options) => {
	track('cap-wrong-account-screen-use-existing-account', null, options);
};

export const trackWrongAccountScreenSwitchAccount = (options) => {
	track('cap-wrong-account-screen-use-switch-account', null, options);
};

// Discount Dialog
export const trackDiscountCouponShown = (options) => {
	track('cap-discount-coupon-shown', null, options);
};

export const trackDiscountCouponError = (options) => {
	track('cap-discount-coupon-error', null, options);
};

export const trackInvalidSerial = (options) => {
	track('cap-magix-validation-invalid-serial', null, options);
};

export const trackEmailSerialMissmatch = (options) => {
	track('cap-magix-validation-email-serial-missmatch', null, options);
};

/* Contact Sales Modal */ 
export const trackContactSalesPopupShown = (options) => {
	track('cap-contact-sales-modal-shown', null, options);
};

/* Add Users Modal */ 
export const trackAddUsersPopupShown = (options) => {
	track('cap-add-users-modal-shown', null, options);
};

/* Confirm Purchase Choice popup */
export const trackConfirmPurchaseChoicePopupShown = (plan, options) => {
	track('cap-confirm-purchase-choice-popup-shown', { plan: getPlanNameForTracking(plan) }, options);
};

/* Buy more seats / licenses */
export const trackBuyMoreSeatsPopupShown = (plan, options) => {
	track('cap-buy-more-seats-popup-shown', { plan: getPlanNameForTracking(plan) }, options);
};

export const trackBuyMoreLicensesPopupShown = (plan, options) => {
	track('cap-buy-more-licenses-popup-shown', { plan: getPlanNameForTracking(plan) }, options);
};

/* Reduce users */
export const trackReduceUsersModal = (options) => {
	track('cap-reduce-users-popup-shown', null, options);
};

export const trackReducedUsers = (nrOfUsersToTurnToGuest, nrOfUsersToDelete, options) => {
	track('cap-reduced-users', { nrOfUsersToTurnToGuest, nrOfUsersToDelete }, options);
	trackReducedUsersData(nrOfUsersToTurnToGuest, nrOfUsersToDelete, options);
};

// Plan configurator change
export const trackQuantityChange = _.debounce((quantityType, value, options) => {
	track(`cap-change-quantity-${quantityType}`, { quantity: value }, options);
	trackConfiguratorQuantity(quantityType, value, options);
}, 1000);

export const trackCategoryChange = (category, options) => {
	track(`cap-category-change-${category.toLowerCase().replace(/ /g,'-')}`, null, options);
};

export const trackDurationChange = (period, options) => {
	track(`cap-duration-change-${period}`, null, options);
};

export const trackUsersLimitReachedOnNonPayingAccount = (options) => {
	track('cap-users-limit-reached-non-paying-account', null, options);
};


// WEBPACK FOOTER //
// ./src/lib/Segment/customTrackers.js